/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React from "react";
import { Canceler } from "axios";
//custom libraries
import { useCustomerHandler } from "./rest";
import addressHelper from "../helpers/addressHelper";
import { useProgressValue } from "../context";

export interface EditFormData {
  id?: any;
  logonId?: string;
  firstName?: string;
  lastName?: string;
  email1?: string;
  phone1?: any;
  address1?: string;
  zipCode?: string;
  state?: string;
  country?: string;
}

export const initEditForm: EditFormData = {
  id: "",
  logonId: "",
  firstName: "",
  lastName: "",
  email1: "",
  phone1: "",
  address1: "",
  zipCode: "",
  state: "",
  country: "",
};

/**
 * A utility hook that handle customer request without any
 * Global context involved.
 */
export const useCustomer = () => {
  const isV9 = process.env.COMMERCE_VERSION === "9";

  const {
    updateCustomerRequest,
    resetPasswordRequest,
    resetPasswordV2Request,
    getCustomerByIdRequest,
    updateCustomerInfo,
    getCustomerByIdV2Request,
    getCustomerByLogonIdRequest,
    updateCustomerV2Request,
  } = useCustomerHandler();

  const [openMsg, setOpenMsg] = React.useState<boolean>(false);
  const [displayAlert, setDisplayAlert] = React.useState<boolean>(false);
  const [cusinfo, setCusinfo] = React.useState<any>({});
  const [canSave, setCanSave] = React.useState<boolean>(false);
  const [editFormData, setEditFormData] = React.useState<EditFormData | any>(
    initEditForm
  );

  const { setInProgress } = useProgressValue();

  const cancels: any = { cancels: [] };

  const fetchCustomer = (id: string) => {
    const [res, canceler] = getCustomerByIdRequest(id);
    cancels.cancels = canceler;
    return res
      .then((r) => {
        setCusinfo(r.data);
        return r.data;
      })
      .catch((e) => console.log(e));
  };

  const fetchCustomerV2 = async (id: string) => {
    const [res, canceler] = getCustomerByIdV2Request(id);
    cancels.cancels = canceler;
    try {
      let data = (await res).data;
      const [cusinfoRes, cusinfoCanceler] = getCustomerByLogonIdRequest(
        data.userRegistry.logonId
      );
      cancels.cancels = cusinfoCanceler;
      data.customerDetails = (await cusinfoRes).data.resultList[0];

      setCusinfo(data);
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  const handleChange = (event: any) => {
    const name = event.target.name;
    const value = event.target.value;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const checkSaveAction = (editFormData: any) => {
    let status: boolean = false;

    let didFormDataChange: boolean =
      editFormData?.address1 !== cusinfo?.address?.address1 ||
      editFormData?.zipCode !== cusinfo?.address?.zipCode ||
      editFormData?.state !== cusinfo?.address?.state ||
      editFormData?.country !== cusinfo?.address?.country ||
      editFormData?.firstName !== cusinfo?.address?.firstName ||
      editFormData?.lastName !== cusinfo?.address?.lastName ||
      editFormData?.email1 !== cusinfo?.address?.email1 ||
      editFormData?.phone1 !== cusinfo?.address?.phone1;

    status =
      didFormDataChange &&
      editFormData?.address1 &&
      editFormData?.lastName &&
      editFormData?.zipCode &&
      editFormData?.state &&
      editFormData?.country &&
      addressHelper.validatePhoneNumber(editFormData?.phone1) &&
      addressHelper.validateEmail(editFormData?.email1);

    setCanSave(status);
  };

  const toggleCustomerStatus = (customer: any) => {
    const body = isV9
      ? { status: customer?.status === 1 ? 0 : 1 }
      : { userStatus: customer?.userRegistry?.status === "1" ? "0" : "1" };

    let userId = customer.id || customer.userId;
    const [res, canceler] = isV9
      ? updateCustomerRequest(body, userId)
      : updateCustomerV2Request(body, userId);
    cancels.cancels = canceler;
    return res;
  };

  const clickResetPassword = (customer: any) => {
    if (isV9) {
      const [canceler] = resetPasswordRequest(customer.id);
      cancels.cancels = canceler;
    } else {
      setInProgress(true);
      const [res, canceler] = resetPasswordV2Request(customer.logonId);
      cancels.cancels = canceler;
      res
        .then((r: any) => {
          if (r.data.viewTaskName) {
            setOpenMsg(true);
          }
        })
        .catch((e) => console.log(e))
        .finally(() => {
          setInProgress(false);
        });
    }
  };

  const updateCustomer = () => {
    let cusData: any = filterUnchangedValues(editFormData);
    const [res, canceler] = updateCustomerInfo(cusData, cusData?.id);
    cancels.cancels = canceler;
    return res;
  };

  const filterUnchangedValues = (editFormData: any): any => {
    if (editFormData?.address1 === cusinfo?.address?.address1) {
      delete editFormData.address1;
    }
    if (editFormData?.country === cusinfo?.address?.country) {
      delete editFormData.country;
    }
    if (editFormData?.email1 === cusinfo?.address?.email1) {
      delete editFormData.email1;
    }
    if (editFormData?.firstName === cusinfo?.address?.firstName) {
      delete editFormData.firstName;
    }
    if (editFormData?.lastName === cusinfo?.address?.lastName) {
      delete editFormData.lastName;
    }
    if (editFormData?.logonId) {
      delete editFormData.logonId;
    }
    if (editFormData?.phone1 === cusinfo?.address?.phone1) {
      delete editFormData.phone1;
    }
    if (editFormData?.state === cusinfo?.address?.state) {
      delete editFormData.state;
    }
    if (editFormData?.zipCode === cusinfo?.address?.zipCode) {
      delete editFormData.zipCode;
    }
    return editFormData;
  };

  const initializeEditForm = (customerInfo: any) => {
    const data = {
      id: customerInfo?.userId,
      logonId: customerInfo?.logonId,
      firstName: customerInfo?.address?.firstName,
      lastName: customerInfo?.address?.lastName,
      email1: customerInfo?.address?.email1,
      phone1: customerInfo?.address?.phone1,
      address1: customerInfo?.address?.address1,
      zipCode: customerInfo?.address?.zipCode,
      state: customerInfo?.address?.state,
      country: customerInfo?.address?.country,
    };
    setEditFormData({ ...data });
  };
  React.useEffect(() => {
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  React.useEffect(() => {
    checkSaveAction(editFormData);
  }, [editFormData]);

  return {
    toggleCustomerStatus,
    clickResetPassword,
    fetchCustomer,
    cusinfo,
    setCusinfo,
    handleChange,
    updateCustomer,
    canSave,
    editFormData,
    setEditFormData,
    initializeEditForm,
    displayAlert,
    setDisplayAlert,
    fetchCustomerV2,
    openMsg,
    setOpenMsg,
  };
};
